import moment from "moment";
import JSZip from 'jszip';

// ============================================================================
// Currency & Number Formatting
// ============================================================================

/**
 * Gets the currency symbol from environment variables or defaults to "$".
 * 
 * @returns {string} The currency symbol (defaults to "$" if not set in env)
 */
export function getCurrencySymbol() {
  const symbol = process.env.VUE_APP_CURRENCY_SYMBOL;
  return symbol || "$"; // Default to dollar sign if not set
}

/**
 * Formats a number with abbreviated notation (B, M, K suffixes).
 * - Values >= 1B: displays as "X.XB" (e.g., "1.5B")
 * - Values >= 1M: displays as "X.XM" (e.g., "2.3M")
 * - Values >= 1K: displays as "X.XK" (e.g., "5.2K")
 * - Values < 1K: returns formatted with comma separators (e.g., "1,234")
 * 
 * @param {number} num - The number to format
 * @returns {string} Formatted number string with abbreviation or comma separators
 */
export function numFormatter(num) {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + "B";
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  // Use toLocaleString for better formatting of small numbers with commas
  return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * Formats a numeric amount as currency with abbreviated notation.
 * Uses the currency symbol from environment variables and applies number formatting.
 * - Values >= 1B: displays as "X.XB" (e.g., "$1.5B")
 * - Values >= 1M: displays as "X.XM" (e.g., "$2.3M")
 * - Values >= 1K: displays as "X.XK" (e.g., "$5.2K")
 * - Values < 1K: displays with comma separators (e.g., "$1,234")
 * - Zero/null/negative values: returns currency symbol + "0"
 * 
 * @param {number} amount - The numeric amount to format
 * @returns {string} Formatted currency string with abbreviation (e.g., "$1.5M", "$5.2K", "$1,234")
 */
export function formattedCurrency(amount) {
  if (amount > 0) {
    return getCurrencySymbol() + numFormatter(amount);
  }
  return getCurrencySymbol() + "0";
}

/**
 * Formats a numeric amount as full currency with comma separators (no abbreviation).
 * Rounds the amount to the nearest integer and adds thousand separators.
 * 
 * @param {number} amount - The numeric amount to format
 * @returns {string|undefined} Formatted currency string with commas (e.g., "$1,234,567") or undefined if invalid
 */
export function formattedCurrencyFull(amount) {
  if (!isNaN(amount)) {
    return (
      getCurrencySymbol() +
      Math.round(amount)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    );
  }
}

/**
 * Formats a numeric value as currency with abbreviated notation.
 * This is a convenience wrapper around formattedCurrency() that handles null/undefined values.
 * Uses the currency symbol from environment variables.
 * - Values >= 1B: displays as "X.XB" (e.g., "$1.5B")
 * - Values >= 1M: displays as "X.XM" (e.g., "$1.5M")
 * - Values >= 1K: displays as "X.XK" (e.g., "$5.2K")
 * - Values < 1K: displays with comma separators (e.g., "$1,234")
 * - Zero/null values: returns currency symbol + "0"
 * 
 * @param {number} value - The numeric value to format
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value) {
  if (!value) {
    return getCurrencySymbol() + "0";
  }
  return formattedCurrency(value);
}

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Formats a date value as "Month YYYY" (e.g., "January 2024").
 * 
 * @param {string|Date} date_value - The date value to format
 * @returns {string|null} Formatted date string or null if date_value is falsy
 */
export function getMonthYear(date_value) {
  if (!date_value) {
    return null;
  }
  return moment(date_value).format("MMMM YYYY");
}

/**
 * Formats a date value as "YYYY/M/D" (e.g., "2024/1/15").
 * 
 * @param {string|Date} date_value - The date value to format
 * @returns {string|null} Formatted date string or null if date_value is falsy
 */
export function formatDate(date_value) {
  if (!date_value) {
    return null;
  }
  return moment(date_value).format("YYYY/M/D");
}

/**
 * Formats a date value as a readable string (e.g., "Jan 15, 2024").
 * Handles invalid dates gracefully by returning the original string.
 * 
 * @param {string|Date} dateStr - The date value to format
 * @returns {string} Formatted date string (e.g., "Jan 15, 2024") or empty string if falsy, or original string if invalid
 */
export function formatDateReadable(dateStr) {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return String(dateStr);
  }
}

/**
 * Calculates the percentage of a value relative to a total.
 * 
 * @param {number} value - The value to calculate percentage for
 * @param {number} total - The total value to compare against
 * @returns {string} Percentage string with one decimal place (e.g., "45.5%")
 */
export function getPercent(value, total) {
  return (value / total * 100).toFixed(1) + '%';
}

// ============================================================================
// Color Utilities
// ============================================================================

/**
 * Generates a hexadecimal color based on a transparency score.
 * Higher scores result in darker colors (lower intensity), lower scores result in lighter colors.
 * Intensity ranges from 0 (highest scores) to 0.7 (lowest scores).
 * 
 * @param {number} score - The transparency score (higher = darker color)
 * @returns {string} Hexadecimal color string (e.g., "#ff1463")
 */
export function benchmarkTransparencyColor(score) {
  // Calculate intensity based on the score (0 for highest scores, 0.7 for lowest scores)
  let intensity = 0.7 - (score - 1) * 0.007;
  
  // Ensure intensity is within the range [0, 0.7]
  intensity = Math.max(0, Math.min(0.7, intensity));
  
  // Calculate red, green, and blue values based on the intensity
  const red = Math.round(255 * intensity);
  const green = Math.round(20 * intensity);
  const blue = Math.round(99 * intensity);
  
  // Create the hexadecimal color
  const color = "#" + 
    red.toString(16).padStart(2, "0") + 
    green.toString(16).padStart(2, "0") + 
    blue.toString(16).padStart(2, "0");
  
  return color;
}

/**
 * Generates an RGBA color with variable alpha based on a transparency score.
 * Higher scores result in higher opacity (alpha closer to 1), lower scores result in lower opacity.
 * Alpha ranges from 1 (highest scores) to 0.3 (lowest scores).
 * 
 * @param {number} score - The transparency score (higher = more opaque)
 * @returns {string} RGBA color string (e.g., "rgba(18, 37, 58, 0.8)")
 */
export function checkTransparencyColor(score) {
  // Calculate alpha based on the score (1 for highest scores, 0.3 for lowest scores)
  let alpha = 1 - (score - 1) * 0.007;
  
  // Ensure alpha is within the range [0.3, 1]
  alpha = Math.max(0.3, Math.min(1, alpha));
  
  // Create the rgba color using the #ff1463 color with the calculated alpha
  return `rgba(18, 37, 58, ${alpha})`;
}

/**
 * Returns a color based on a score using a color-coded scale.
 * Color progression: Red (lowest) -> Orange -> Gold -> Lime Green -> Green (highest)
 * 
 * @param {number} score - The score to determine color (typically 0-100)
 * @returns {string} Hexadecimal color string based on score range
 */
export function transparencyColor(score) {
  let color = "#143522"; // Default color from the palette (darkest shade)
  
  switch (true) {
    case score <= 30:
      color = "#d42613"; // Red - lowest scores
      break;
    case score <= 50:
      color = "#d48a13"; // Orange-red
      break;
    case score <= 60:
      color = "#8a9917"; // Orange-red
      break;
    case score <= 70:
      color = "#729917"; // Gold
      break;
    case score <= 80:
      color = "#1c9c1e"; // Lime Green
      break;
    case score <= 100:
      color = "#1d5e39"; // Green - highest scores
      break;
  }
  
  return color;
}

/**
 * Returns a color associated with a government/jurisdiction name.
 * Supports Australian states/territories and UK regions.
 * 
 * @param {string} gov - The government/jurisdiction name (case-insensitive)
 * @returns {string} Hexadecimal color string for the jurisdiction, or "magenta" if unknown
 */
export function govColor(gov) {
  gov = gov !== undefined ? gov.toLowerCase() : undefined;
  let color = "#808080";
  
  switch (gov) {
    case "nsw":
      color = "#41a7d9";
      break;
    case "vic":
      color = "#0058AC";
      break;
    case "wa":
      color = "#FFD200";
      break;
    case "qld":
      color = "#A6003D";
      break;
    case "aus":
    case "defence":
    case "fed":
      color = "#D8993B";
      break;
    case "def":
      color = "#d8673b";
      break;
    case "sa":
      color = "#ff4081";
      break;
    case "tas":
      color = "#4eb888";
      break;
    case "act":
    case "local":
      color = "#41a7d9";
      break;
    case "nt":
    case "central":
      color = "#FF3647";
      break;
    case "wales":
      color = "#4eb888";
      break;
    case "scotland":
      color = "#ff4081";
      break;
    case "northern ireland":
      color = "#d8673b";
      break;
    default:
      color = "magenta";
      break;
  }
  
  return color;
}

// ============================================================================
// Data Transformation Utilities
// ============================================================================

/**
 * Safely parses JSON data, handling both objects and JSON strings.
 * If input is already an object, returns a deep copy. If it's a string, parses it.
 * 
 * @param {object|string} qry - The data to parse (object or JSON string)
 * @returns {object} Parsed object or deep copy of input object
 */
export function parseJSON(qry) {
  if (typeof qry === 'object' && qry !== null && !Array.isArray(qry)) {
    return JSON.parse(JSON.stringify(qry)); // Return as is if it's already an object
  } else if (typeof qry === 'string') {
    return JSON.parse(qry); // Convert string to an object
  }
  return qry;
}

/**
 * Gets the key at a specific index from an object.
 * Iterates through object keys and returns the key at the specified zero-based index.
 * 
 * @param {object} obj - The object to get the key from
 * @param {number} index - The zero-based index of the key to retrieve
 * @returns {string|null} The key at the specified index, or null if index is out of range
 */
export function getKey(obj, index) {
  let i = 0;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (i === index) {
        return key;
      }
      i++;
    }
  }
  return null;
}

/**
 * Converts a value to an array if it's not already one.
 * If input is already an array, returns it as-is. If it's a non-empty string, wraps it in an array.
 * Otherwise returns the original value.
 * 
 * @param {any} string - The value to convert (can be array, string, or other)
 * @returns {Array|any} Array containing the value, or original value if conversion not needed
 */
export function getArray(string) {
  if (Array.isArray(string)) {
    return string;
  }
  if (string > "") {
    return [string];
  }
  return string;
}

/**
 * Formats a percentage change value with associated icon type and color.
 * Returns an object with icon type (mdi-menu-up, mdi-menu-down, mdi-minus) and color (green, red, black).
 * 
 * @param {number} per_change - The percentage change value (can be positive, negative, or zero)
 * @returns {object} Object with properties: {icontype: string, color: string, change: number}
 */
export function growth_format(per_change) {
  let icontype = String;
  let color = String;
  
  if (per_change == 0) {
    icontype = "mdi-minus";
    color = "black";
    per_change = per_change * -1;
  } else if (per_change > 0) {
    icontype = "mdi-menu-up";
    color = "green";
  } else if (per_change < 0) {
    icontype = "mdi-menu-down";
    color = "red";
    per_change = per_change * -1;
  }
  
  return { icontype: icontype, color: color, change: per_change };
}

/**
 * Removes all HTML tags from a text string, returning plain text.
 * Uses a regular expression to match and remove all HTML tags (e.g., <div>, </p>, <span class="...">).
 * 
 * @param {string} text - The text string that may contain HTML tags
 * @returns {string} Plain text string with all HTML tags removed, or empty string if input is falsy
 */
export function stripHTML(text) {
  if (!text) return "";
  return text.replace(/(<([^>]+)>)/gi, "");
}

// ============================================================================
// Search & Matching Utilities
// ============================================================================

/**
 * Extracts matched fields from an object containing HTML with <em> tags.
 * Finds all text within <em> tags, extracts unique words, and filters them based on URL query parameters.
 * 
 * @param {object} obj - Object containing arrays of HTML strings with <em> tags
 * @returns {Array} Array of unique matched words that appear in URL query parameters
 */
export function getMatchedFields(obj) {
  const result = new Set();
  
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const arr = obj[key];
      arr.forEach((value) => {
        const matches = value.match(/<em>([^<]*)<\/em>/gi);
        if (matches) {
          const words = matches.map((match) =>
            match.replace(/<\/?em>/gi, '').trim().toLowerCase()
          );
          
          const uniqueArr = [...new Set(words)];
          const concatenatedWords = uniqueArr.join(' ');
          
          if (!result.has(concatenatedWords)) {
            result.add(concatenatedWords);
          }
        }
      });
    }
  }
  
  const queryString = window.location.search;
  return filterMatchesByQuery(queryString, Array.from(result));
}

/**
 * Filters an array of matches based on URL query parameters.
 * Returns only matches that are found within any of the query parameter values.
 * 
 * @param {string} queryString - The URL query string (e.g., "?term=value")
 * @param {Array<string>} matches - Array of match strings to filter
 * @returns {Array<string>} Filtered array of matches that appear in query parameters
 */
function filterMatchesByQuery(queryString, matches) {
  const queryTerms = new URLSearchParams(queryString);
  const filteredMatches = matches.filter((match) => {
    const lowerMatch = match.toLowerCase();
    return Array.from(queryTerms.values()).some(
      (value) => value.toLowerCase().includes(lowerMatch)
    );
  });
  return filteredMatches;
}

// ============================================================================
// URL Utilities
// ============================================================================

/**
 * Updates the browser URL query parameters without triggering a route change event.
 * Useful for updating URL state without causing navigation or component re-renders.
 * 
 * @param {Array<object>} settings - Array of objects with "field_name" and "value" properties
 */
export function silentURLUpdate(settings) {
  // Quietly update the URL without triggering a route changed event
  const url = new URL(window.location);
  
  for (const i in settings) {
    url.searchParams.set(settings[i]["field_name"], settings[i]["value"]);
  }
  
  window.history.pushState({}, '', url);
}

// ============================================================================
// File Download Utilities
// ============================================================================

/**
 * Triggers a file download for an image source.
 * Creates a temporary anchor element, sets it to download the file, and programmatically clicks it.
 * 
 * @param {string} src - The source URL of the file to download
 */
export function downloadFile(src) {
  // Generate a temp <a /> tag for download
  const link = document.createElement("a");
  link.href = src;
  link.target = "_blank";
  link.style = "visibility:hidden";
  link.download = "tendertrace_chart.png";
  
  // Append, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// ============================================================================
// CSV Export Utilities
// ============================================================================

/**
 * Converts an array of objects to CSV format string.
 * Handles escaping of commas, quotes, and newlines in values.
 * 
 * @param {Array<object>|string} objArray - Array of objects or JSON string to convert
 * @returns {string} CSV formatted string with CRLF line endings
 */
export function convertToCSV(objArray) {
  const array = typeof objArray != "object" ? JSON.parse(objArray) : objArray;
  let str = "";
  
  for (let i = 0; i < array.length; i++) {
    let line = "";
    for (const index in array[i]) {
      if (line != "") line += ",";
      
      const value = array[i][index];
      // Convert to string, handling null/undefined
      let stringValue = value == null ? "" : String(value);
      
      // Check if value contains comma, quote, or newline
      if (stringValue.includes(",") || stringValue.includes('"') || 
          stringValue.includes("\n") || stringValue.includes("\r")) {
        // Escape double quotes by doubling them, then wrap in quotes
        stringValue = '"' + stringValue.replace(/"/g, '""') + '"';
      }
      
      line += stringValue;
    }
    
    str += line + "\r\n";
  }
  
  return str;
}

/**
 * Exports data as a CSV file and triggers browser download.
 * Supports both modern browsers (HTML5 download) and IE 10+ (msSaveBlob).
 * 
 * @param {object} headers - Object representing CSV headers (optional)
 * @param {Array<object>} items - Array of objects to export as CSV rows
 * @param {string} fileTitle - The filename for the downloaded CSV (without .csv extension)
 */
export function exportCSVFile(headers, items, fileTitle) {
  if (headers) {
    items.unshift(headers);
  }
  
  // Convert Object to JSON
  const jsonObject = JSON.stringify(items);
  const csv = convertToCSV(jsonObject);
  const exportedFilename = fileTitle + ".csv" || "export.csv";
  
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, exportedFilename);
  } else {
    const link = document.createElement("a");
    if (link.download !== undefined) {
      // Feature detection - browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", exportedFilename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

/**
 * Formats tender/contract data for CSV export.
 * Maps internal field names to CSV column names and applies default values for missing fields.
 * Removes commas and newlines from text fields to ensure valid CSV format.
 * 
 * @param {Array<object>} data - Array of tender/contract data objects
 * @returns {object} Object with "headers" (field mapping) and "itemsFormatted" (formatted data array)
 */
function formatDataForCSV(data) {
  const headers = {
    id: "id",
    recid: "reference",
    disposition: "disposition",
    title: "title",
    description: "description",
    value: "value",
    original_value: "original_value",
    url: "source",
    government: "government",
    publisher: "buyer",
    publisher_industry: "buyer_industry",
    publisher_employees: "buyer_employees",
    supplier: "supplier",
    supplier_group_name: "supplier_group_name",
    supplier_company_number: "abn",
    published_date: "published_date",
    start_date: "start_date",
    start_fy: "start_fy",
    start_qtr: "start_quarter",
    start_month: "start_month",
    start_year: "start_year",
    expiry_date: "expiry_date",
    expiry_fy: "expiry_fy",
    expiry_qtr: "expiry_quarter",
    expiry_month: "expiry_month",
    expiry_year: "expiry_year",
    parent_category_code: "parent_category_code",
    parent_category: "parent_category",
    category_code: "category_code",
    category: "category",
    is_consultancy_services: "consultancy",
    contact_person: "contact",
  };
  
  const itemsNotFormatted = data;
  const itemsFormatted = [];
  
  // Format the data
  for (let item = 0; item < itemsNotFormatted.length; item++) {
    try {
      // Set defaults for missing fields
      const itemData = itemsNotFormatted[item];
      const defaults = {
        description: "",
        value: "",
        original_value: "",
        publisher: "",
        publisher_industry: "",
        publisher_employees: "",
        supplier: "",
        supplier_group_name: "",
        contract_start: "",
        au_contract_fy: "",
        au_contract_qtr: "",
        contract_month: "",
        contract_year: "",
        expiry_date: "",
        au_expiry_fy: "",
        au_expiry_qtr: "",
        expiry_month: "",
        expiry_year: "",
        contact_person: "",
        supplier_company_number: "",
        url: "",
        parent_category_code: "",
        parent_category: "",
        category: "",
        category_code: "",
        is_consultancy_services: "",
        published_date: "",
      };
      
      // Apply defaults
      Object.keys(defaults).forEach(key => {
        if (!itemData[key]) {
          itemData[key] = defaults[key];
        }
      });
      
      itemsFormatted.push({
        id: itemData.id,
        recid: itemData.recid,
        disposition: itemData.disposition,
        title: itemData.title.replace(/[,\n]+/g, ""),
        description: itemData.description.replace(/[,\n]+/g, ""),
        value: itemData.value,
        original_value: itemData.original_value,
        url: itemData.url,
        government: itemData.government,
        publisher: itemData.publisher.replace(/[,\n]+/g, ""),
        publisher_industry: itemData.publisher_industry.replace(/[,\n]+/g, ""),
        publisher_employees: itemData.publisher_employees.replace(/[,\n]+/g, ""),
        supplier: itemData.supplier.replace(/[,\n]+/g, ""),
        supplier_group_name: itemData.supplier_group_name.replace(/[,\n]+/g, ""),
        supplier_company_number: itemData.supplier_company_number,
        published_date: itemData.published_date,
        start_date: itemData.contract_start,
        start_fy: itemData.au_contract_fy,
        start_qtr: itemData.au_contract_qtr,
        start_month: itemData.contract_month,
        start_year: itemData.contract_year,
        expiry_date: itemData.expiry_date,
        expiry_fy: itemData.au_expiry_fy,
        expiry_qtr: itemData.au_expiry_qtr,
        expiry_month: itemData.expiry_month,
        expiry_year: itemData.expiry_year,
        parent_category_code: itemData.parent_category_code,
        parent_category: itemData.parent_category.replace(/[,\n]+/g, ""),
        category_code: itemData.category_code,
        category: itemData.category.replace(/[,\n]+/g, ""),
        is_consultancy_services: itemData.is_consultancy_services.replace(/[,\n]+/g, ""),
        contact_person: itemData.contact_person.replace(/[,\n]+/g, ""),
      });
    } catch (err) {
      continue;
    }
  }
  
  return { headers, itemsFormatted };
}

/**
 * Downloads all pages of data as a single combined CSV file.
 * Combines data from multiple pages into one CSV with a timestamped filename.
 * 
 * @param {Array<object>} pagesData - Array of objects with "data" property containing page data
 */
export function downloadPagesAsSingleCSV(pagesData) {
  // Combine all pages' data into a single array
  const allData = [];
  for (const pageInfo of pagesData) {
    if (pageInfo.data && Array.isArray(pageInfo.data)) {
      allData.push(...pageInfo.data);
    }
  }
  
  if (allData.length === 0) {
    return;
  }
  
  // Format the combined data
  const { headers, itemsFormatted } = formatDataForCSV(allData);
  
  // Create file title with timestamp
  const dte = new Date();
  const timestamp = dte.toISOString().replace(/[:.]/g, '-').slice(0, -5);
  const fileTitle = `tendertrace_${timestamp}`;
  
  // Download as single CSV file
  exportCSVFile(headers, itemsFormatted, fileTitle);
}

/**
 * Downloads all pages of data as separate CSV files in a ZIP archive.
 * Each page becomes a separate CSV file named "page_N.csv" within the ZIP.
 * 
 * @param {Array<object>} pagesData - Array of objects with "page" (number) and "data" (array) properties
 * @returns {Promise<void>} Promise that resolves when download is triggered
 */
export async function downloadPagesAsZip(pagesData) {
  // pagesData should be an array of { page: number, data: array }
  const zip = new JSZip();
  
  const dte = new Date();
  const timestamp = dte.toISOString().replace(/[:.]/g, '-').slice(0, -5);
  
  // Process each page's data
  for (const pageInfo of pagesData) {
    if (!pageInfo.data || pageInfo.data.length === 0) {
      continue;
    }
    
    const { headers, itemsFormatted } = formatDataForCSV(pageInfo.data);
    
    // Create CSV content
    const csvItems = [headers, ...itemsFormatted];
    const jsonObject = JSON.stringify(csvItems);
    const csv = convertToCSV(jsonObject);
    
    // Add CSV file to zip
    const fileName = `page_${pageInfo.page}.csv`;
    zip.file(fileName, csv);
  }
  
  // Generate zip file
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  
  // Download the zip file
  const zipFileName = `tendertrace_pages_${timestamp}.zip`;
  
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(zipBlob, zipFileName);
  } else {
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(zipBlob);
      link.setAttribute("href", url);
      link.setAttribute("download", zipFileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Clean up the object URL after a short delay
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }
  }
}

/**
 * Downloads a single page of data as a CSV file.
 * Creates a timestamped filename including the page number.
 * 
 * @param {Array<object>} data - Array of data objects to export
 * @param {number} page - The page number to include in the filename
 */
export function download(data, page) {
  const { headers, itemsFormatted } = formatDataForCSV(data);
  
  const dte = new Date();
  const fileTitle = "tendertrace_page" + page + "_" + dte.toISOString();
  
  exportCSVFile(headers, itemsFormatted, fileTitle);
}